'use client';

import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { TeaseAnalyticsProvider, type TeaseAnalytics } from '@sharpr/ui';
import type { Sport } from '@sharpr/types';
import {
  captureEvent,
  EVENTS,
  type UpgradePromptSurface,
  type UpgradeViewer,
} from '@/lib/posthog/events';

export interface UpgradePromptTracker {
  trackView: (surface: UpgradePromptSurface) => void;
  trackClick: (surface: UpgradePromptSurface, action: 'cta' | 'secondary', destination: string) => void;
}

/**
 * upgrade_prompt_viewed / upgrade_prompt_clicked for the odds screen — the replacement for
 * the paywall_hit signal lost when odds went public. Views dedupe per surface for the life
 * of the odds screen (one blurred-EV popover per row would otherwise fire hundreds of
 * views per visit); clicks always fire.
 */
export function useUpgradePromptTracker(sport: Sport | null, viewer: UpgradeViewer): UpgradePromptTracker {
  const seen = useRef(new Set<string>());
  const trackView = useCallback(
    (surface: UpgradePromptSurface) => {
      const key = `${sport}:${surface}`;
      if (seen.current.has(key)) return;
      seen.current.add(key);
      captureEvent(EVENTS.UPGRADE_PROMPT_VIEWED, { surface, sport, viewer });
    },
    [sport, viewer],
  );
  const trackClick = useCallback(
    (surface: UpgradePromptSurface, action: 'cta' | 'secondary', destination: string) =>
      captureEvent(EVENTS.UPGRADE_PROMPT_CLICKED, { surface, sport, viewer, action, destination }),
    [sport, viewer],
  );
  return useMemo(() => ({ trackView, trackClick }), [trackView, trackClick]);
}

/** Routes the shared UI package's tease surfaces (TeaseUpsell / TeaseSurface) into the tracker. */
export function UpgradePromptAnalyticsProvider({
  tracker,
  children,
}: {
  tracker: UpgradePromptTracker;
  children: ReactNode;
}) {
  const value = useMemo<TeaseAnalytics>(
    () => ({
      onView: (surface) => tracker.trackView(surface as UpgradePromptSurface),
      onClick: (surface, action, href) => tracker.trackClick(surface as UpgradePromptSurface, action, href),
    }),
    [tracker],
  );
  return <TeaseAnalyticsProvider value={value}>{children}</TeaseAnalyticsProvider>;
}

/** Entitlement bucket for upsell analytics. */
export function upgradeViewerOf(isSignedIn: boolean, ownedSportCount: number): UpgradeViewer {
  if (!isSignedIn) return 'anonymous';
  return ownedSportCount > 0 ? 'subscriber_other_sport' : 'logged_in_free';
}
